import React, { useCallback } from 'react';
import { MdClose } from 'react-icons/md';
import { FileDrop, Modal } from '~/components';
import { FormGroup, TextArea } from '~/components/form';
import { useForm } from '~/hooks';
import { DocumentType } from '~/api/document';

const deedState = {
    sectionId: 0,
    sectionDetailId: 0,
    titleOpinionId: 0,
    grantors: '',
    grantees: '',
    purchasePrice: null,
    dated: null,
    recordedOn: null,
    book: '',
    page: '',
    countyRegistry: '',
    copyOfDeed: []
};

const DeedForm = ({ isOpen, edit = false, readonly = false, deed = {}, saveDeed, closeModal }) => {

    const onLoad = useCallback(async (values, props) => {
        if (props.edit) {
            const { deed } = props;
            let copyOfDeed = [];

            if (Array.isArray(deed.documents)) {
                copyOfDeed = deed.documents.map(d => {
                    return { 
                        file: { 
                            path: d.name, 
                        }, 
                        type: d.typeId, 
                        uploaded: true, 
                        isDeleted: false ,
                        ...d
                    };
                });
            }

            return { 
                ...values,
                sectionId: props.deed.sectionId,
                sectionDetailId: props.deed.sectionDetailId,
                ...props.deed._data, 
                copyOfDeed };
        } 
        
        return { ...values };
    }, [isOpen, edit]);

    const {
        state,
        fieldActions,
        formActions
    } = useForm({
        initialState: deedState,
        onLoad,
        props: { edit, deed }
    });

    return (
        <Modal isModalOpen={isOpen}  preventScroll className='w-3/4 h-3/4 bg-white rounded-md' >
            <section className='h-full flex flex-col'>
                <header className='pt-3 pb-2 px-8 border-b flex items-center justify-between'>
                    <p className='font-semibold'>Deed</p>
                    <button 
                        className='rounded-full p-2 hover:bg-gray-150 focus:outline-none'
                        onClick={closeModal}
                    >
                        <MdClose />
                    </button>
                </header>
                <div className='flex-grow overflow-y-scroll'>
                    <div className='px-8 py-4 grid grid-cols-12 grid-flow-row gap-8'>
                        <TextArea
                            label='Grantor(s)'
                            readOnly={readonly}
                            groupClassName='col-span-12'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.grantors}
                            path='grantors'
                            {...fieldActions}
                        />
                        <TextArea
                            label='Grantee(s)'
                            readOnly={readonly}
                            groupClassName='col-span-12'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.grantees}
                            path='grantees'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Purchase Price'
                            readOnly={readonly}
                            type='currency'
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.purchasePrice}
                            path='purchasePrice'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Dated'
                            readOnly={readonly}
                            type='date-picker'
                            dateFormat="MM/dd/yyyy"
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            datePickerClasses="w-full"
                            value={state.values.dated}
                            path='dated'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Recorded On'
                            type='date-picker'
                            readOnly={readonly}
                            dateFormat="MM/dd/yyyy   h:mm aa"
                            showTimeInput
                            timeInputLabel="Time:"
                            datePickerClasses="w-full"
                            groupClassName='col-span-6'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.recordedOn}
                            path='recordedOn'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Book'
                            required
                            readOnly={readonly}
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.book}
                            path='book'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Page'
                            required
                            readOnly={readonly}
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.page}
                            path='page'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='County Registry'
                            required
                            readOnly={readonly}
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.countyRegistry}
                            path='countyRegistry'
                            {...fieldActions}
                        />
                        <FileDrop
                            files={state.values.copyOfDeed.filter(d => !d.isDeleted)}
                            readOnly={readonly}
                            className='w-full h-20'
                            groupClassName='col-span-12'
                            multiple
                            onChange={files => {
                                const _files = files
                                    .map(f => {
                                        return {
                                            ...f, 
                                            type: DocumentType.COPYOFDEED,
                                            uploaded: f.uploaded == undefined ? false : f.uploaded,
                                            isDeleted: f.isDeleted != undefined ? f.isDeleted : false
                                        };
                                    });
                                fieldActions.onChange(_files, 'copyOfDeed');
                            }}
                            onRemove={(file, index) => {
                                let files = [...state.values.copyOfDeed ];
                                files.splice(index, 1);
                                fieldActions.onChange([...files, { ...file, isDeleted: true }], 'copyOfDeed');
                            }}
                            heading='Upload Copy of Deed'
                        />
                        <div className='col-span-12 flex items-center'>
                            { readonly && (
                                <button 
                                    className="ml-auto mr-2 rounded-full text-neutral-90 py-2 px-4 font-medium bg-gray-150 focus:outline-none hover:opacity-60"
                                    onClick={() => {
                                        formActions.reset();
                                        closeModal();
                                    }}
                                >
                                    Close
                                </button>
                            )}
                            { !readonly && (
                                <>
                                    <button 
                                        className="ml-auto mr-2 rounded-full text-neutral-90 py-2 px-4 font-medium bg-gray-150 focus:outline-none hover:opacity-60"
                                        onClick={() => {
                                            formActions.reset();
                                            closeModal();
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        className="rounded-full text-white bg-lightGreen-300 py-2 px-4 font-medium focus:outline-none hover:opacity-60"
                                        onClick={() => saveDeed(state.values)}
                                    >
                                        {edit ? 'Save' : 'Add' }
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </Modal>
    );
};

export default DeedForm;